import {
   DialogContent,
   DialogDescription,
   DialogFooter,
   DialogTitle,
} from "@/Components/ui/dialog";
import { Option, Stage } from "@/types/productTypes";
import { useState } from "react";
import { useAdminStore } from "../../../../store/useAdminStore";

type ImageConfigDialogProps = {
   onClose: () => void;
   currentStage: Stage;
};

export function ImageConfigDialog({
   currentStage,
   onClose,
}: ImageConfigDialogProps) {
   const adminStore = useAdminStore();

   const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>([]);
   const [configName, setConfigName] = useState("");
   const [baseConfigId, setBaseConfigId] = useState<number | null>(null);

   // If you have a list of *all* options:
   const { allOptions } = useAdminStore();

   // Toggle an option ID in the local selectedOptionIds array
   const toggleOption = (option: Option) => {
      const optionId = option.id;
      if (!optionId) return;

      setSelectedOptionIds((prev) =>
         prev.includes(optionId)
            ? prev.filter((id) => id !== optionId)
            : [...prev, optionId]
      );
   };

   /**
    * Decide how to save the config:
    * - If baseConfigId is set, we clone that existing config and append new selectedOptionIds.
    * - Otherwise, we create a brand-new config (the old behavior).
    */
   const saveConfig = async () => {
      if (configName.trim() === "") {
         alert("Config name is required.");
         return;
      }
      const productId = adminStore.selectedProductId;
      const stageId = adminStore.selectedStageId;
      if (!productId || !stageId) return;

      try {
         if (baseConfigId) {
            console.log(
               "Cloning and appending to existing config",
               baseConfigId
            );
            // 1️⃣ Clone & append to an existing config
            await adminStore.cloneAndAppendConfig(
               productId,
               stageId,
               baseConfigId,
               configName,
               selectedOptionIds
            );
         } else {
            console.log("Creating a new config from scratch");
            // 2️⃣ Create a new config from scratch
            await adminStore.addConfig(
               productId,
               stageId,
               configName,
               selectedOptionIds
            );
         }

         onClose();
      } catch (error) {
         console.error("Error saving config:", error);
      }
   };

   return (
      <DialogContent className="max-w-2xl">
         <DialogTitle>Create or Select Image Config</DialogTitle>
         <DialogDescription>
            You can either pick an existing config or create a new one by
            selecting options in a specific order.
         </DialogDescription>

         {/* ───────────────────────────────────────────────────────────────────── */}
         {/* Existing Configurations                                             */}
         {/* ───────────────────────────────────────────────────────────────────── */}
         <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">
               Existing Configurations
            </h3>

            <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 space-y-2">
               {adminStore.currentConfigs.map((config) => (
                  <div
                     key={config.id}
                     className={`p-2 border rounded-md hover:bg-gray-100 
                ${
                   config.id === baseConfigId
                      ? "bg-blue-50 border-blue-300"
                      : ""
                }
              `}
                  >
                     <div className="flex justify-between items-center">
                        <div>
                           <h4 className="text-sm font-medium text-gray-800">
                              {config.name}
                           </h4>
                        </div>

                        <div className="space-x-2">
                           {/* Old "Select" behavior */}
                           <button
                              className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300"
                              onClick={() => {
                                 if (!currentStage.id) return;
                                 if (!adminStore.selectedProductId) return;
                                 if (!adminStore.selectedStageId) return;
                                 // Old: set the stage to use this config directly
                                 adminStore.selectConfig(
                                    adminStore.selectedProductId,
                                    adminStore.selectedStageId,
                                    config.id
                                 );
                                 onClose();
                              }}
                           >
                              Select
                           </button>

                           {/* New "Use as Base" */}
                           <button
                              className="text-xs px-2 py-1 bg-blue-200 rounded hover:bg-blue-300"
                              onClick={() => {
                                 setBaseConfigId(config.id);
                              }}
                           >
                              Use as Base
                           </button>
                        </div>
                     </div>
                  </div>
               ))}
            </div>
         </div>

         {/* ───────────────────────────────────────────────────────────────────── */}
         {/* Create or Clone a New Config                                        */}
         {/* ───────────────────────────────────────────────────────────────────── */}
         <div className="space-y-4 mt-4">
            <input
               type="text"
               placeholder="Config Name"
               value={configName}
               onChange={(e) => setConfigName(e.target.value)}
               className="w-full border border-gray-300 rounded-md px-3 py-2
                     focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            {/* Option selection area */}
            <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-3 space-y-2">
               {allOptions.map((option) => (
                  <div
                     key={option.id}
                     className={`flex items-center justify-between p-2 border rounded-md cursor-pointer 
                ${
                   option.id && selectedOptionIds.includes(option.id)
                      ? "bg-blue-100 border-blue-400"
                      : "hover:bg-gray-100"
                }`}
                     onClick={() => toggleOption(option)}
                  >
                     <span className="text-sm font-medium text-gray-800">
                        {option.name}
                     </span>
                     {option.id && selectedOptionIds.includes(option.id) && (
                        <span className="text-blue-600">
                           {/* Show the index of this option in the selection order */}
                           {selectedOptionIds.findIndex(
                              (id) => id === option.id
                           ) + 1}
                        </span>
                     )}
                  </div>
               ))}
            </div>
         </div>

         <DialogFooter>
            <button
               onClick={onClose}
               className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
               Cancel
            </button>
            <button
               onClick={saveConfig}
               className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
               {baseConfigId ? "Clone & Append Config" : "Save New Config"}
            </button>
         </DialogFooter>
      </DialogContent>
   );
}
